import { useState, type FormEvent } from 'react';
import { KeyRound } from 'lucide-react';
import { useVault } from '../lib/vault';

/**
 * First-time Master Code setup. Handles two branches:
 *   - `!vaultExists` → this is the first user; they bootstrap the
 *     organisation's shared key (today that's the vault key).
 *   - `vaultExists` → an Nth user setting their personal keypair. They'll
 *     land on <InvitationPendingScreen /> until an admin invites them.
 *
 * Lives in Core so every crypto-using app reaches the same prompt via
 * `<IdentityGate>`.
 */
export function MasterCodeSetupScreen() {
  const [masterCode, setMasterCode] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const user = useVault((s) => s.user);
  const vaultExists = useVault((s) => s.vaultExists);
  const setupMasterCode = useVault((s) => s.setupMasterCode);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (masterCode !== confirm) {
      setError('Master Codes do not match.');
      return;
    }
    if (masterCode.length < 12) {
      setError('Master Code must be at least 12 characters.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await setupMasterCode(masterCode);
    } catch (err: any) {
      setError(err?.message ?? 'Master Code setup failed.');
    } finally {
      setLoading(false);
    }
  }

  const isBootstrap = !vaultExists;
  const title = isBootstrap ? 'Create your organisation identity' : 'Set your Master Code';
  const subtitle = isBootstrap
    ? `Welcome ${user?.username}. You're the first user, so your Master Code will create the organisation's shared encryption key. Every other tech invited later will unlock the same shared key with their own Master Code.`
    : `Welcome ${user?.username}. Choose a Master Code — it protects your personal key that grants access to the shared organisation data.`;

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card w-full max-w-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="text-dragon-500" size={24} />
          <h1 className="text-lg font-semibold">{title}</h1>
        </div>
        <p className="text-sm text-ink-300 mb-4">{subtitle}</p>
        <div className="rounded-md border border-amber-900/40 bg-amber-950/20 p-3 text-xs text-amber-200 mb-4">
          <strong>⚠ We cannot recover this code.</strong> Losing it means losing access
          to the shared vault on this device — an admin can re-invite you and a fresh
          code will get you back in (all org data is recoverable; it's encrypted under
          a key shared with every vault member). Memorise it, or write it down somewhere
          physically safe — don't save it in a password manager or any file on this machine.
        </div>
        <form onSubmit={onSubmit} className="space-y-3" autoComplete="off">
          <input
            type="password"
            className="input"
            value={masterCode}
            onChange={(e) => setMasterCode(e.target.value)}
            autoFocus
            placeholder="Master Code (min 12 chars)"
            required
            name="dragonhq-master-code-new"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-1p-ignore
            data-lpignore="true"
            data-bwignore
            data-dashlane-ignore="true"
            data-form-type="other"
          />
          <input
            type="password"
            className="input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm Master Code"
            required
            name="dragonhq-master-code-confirm"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-1p-ignore
            data-lpignore="true"
            data-bwignore
            data-dashlane-ignore="true"
            data-form-type="other"
          />
          {error && (
            <div className="rounded border border-red-900 bg-red-950/40 p-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading ? 'Working…' : isBootstrap ? 'Create organisation identity' : 'Set Master Code'}
          </button>
        </form>
      </div>
    </div>
  );
}
